import {OpenDocumentsPage} from "../pages/DocumentsPage";

let abortControllers = {};

function overrideEnvVarsIfRequested() {
  // Allow process.env.*, if present, to override window._env_.*.
  // This is useful for local development (like "npm start").
  //
  // Note that process.env.* is set at React build/start time if
  // a .env.* file is present, but no .env file is included in
  // built Docker images. In contrast, window._env_.* is set in
  // public/env-config.js, which is overwritten at container launch
  // time to allow the same UI image to run on a variety of hosts.
  //
  // So if process.env.* is set, we're in a local dev environment,
  // and we should respect process.env and override window._env_.*.
  // If it isn't set, then we're running in a container, and
  // we want to leave window._env_.* alone.

  console.log('process.env ', process.env);
  if (process.env.REACT_APP_API) {
    window._env_ = {
      APP_SITE: process.env.REACT_APP_SITE,
      APP_NAME: process.env.REACT_APP_APPLICATION_NAME,
      APP_CHALLENGE_NAME: process.env.REACT_APP_CHALLENGE_NAME,
      APP_URI: process.env.REACT_APP_URI,
      APP_HOSTNAME: process.env.REACT_APP_HOSTNAME,
      APP_NAME_POSSESSIVE: process.env.REACT_APP_APPLICATION_NAME_POSSESSIVE,
      APP_TAG_LINE: process.env.REACT_APP_TAG_LINE,
      APP_SUPPORT_EMAIL: process.env.REACT_APP_SUPPORT_EMAIL,
      APP_API: process.env.REACT_APP_API,
      APP_SECURE_API: null,
      CH_API: process.env.REACT_APP_CH_API,
      CH_SECURE_API: null,
      APP_LOGOUT: process.env.REACT_APP_LOGOUT,
      APP_REGISTER: process.env.REACT_APP_REGISTRATION,
      APP_PRODUCTION: process.env.REACT_APP_PRODUCTION,
      CUI_APP: process.env.REACT_APP_CUI_APP === 'true',
      FEATURE_COHORT: process.env.REACT_APP_FEATURE_COHORT === 'true',
      FEATURE_PREVIEW: true, //process.env.REACT_APP_FEATURE_PREVIEW === 'true',
      FEATURE_AW: true, //process.env.REACT_APP_FEATURE_AW === 'true',
      FEATURE_CLI_DOWNLOAD: process.env.REACT_APP_FEATURE_CLI_DOWNLOAD === 'true',
      FEATURE_CLI_UPLOAD: process.env.REACT_APP_FEATURE_CLI_UPLOAD === 'true',
      FEATURE_ORG_AC: process.env.REACT_APP_FEATURE_ORG_AC === 'true',
      TOOLS_PAGE: true, //process.env.REACT_APP_TOOLS_PAGE === 'true',
      FEATURE_STUDYCARD: process.env.REACT_APP_FEATURE_STUDYCARD === 'true',
      SHOW_DISCLAIMER: true,
      LOCALHOST: process.env.REACT_APP_LOCALHOST === 'true'
    }
  }
  console.log('window._env_ ', window._env_);
}

// Call overrideEnvVarsIfRequested once from global scope so all
// future API clients will have window._env_.* properly initialized.
overrideEnvVarsIfRequested();

function removeLeadingSlash(endpoint) {
  if (endpoint.charAt(0) === '/') {
    return endpoint.substring(1);
  }
  return endpoint;
}

// Use fetch to make a get call to the API.
export const performGetFetch = (endpoint, success, failure, override, printMessage, fullEndpoint) => {
  if (!endpoint) {
    return;
  }
  endpoint = removeLeadingSlash(endpoint);

  // By default we don't want to restart a fetch if one is already running for this endpoint.
  if (!override && abortControllers[endpoint]) {
    if (printMessage) {
      console.warn('Request already in progress: ', endpoint);
    }
    return false;
  }

  let requestOptions = {};

  requestOptions.signal = createAbortController(endpoint, printMessage).signal;
  fullEndpoint = fullEndpoint || buildApiUrl(endpoint);

  return fetch(fullEndpoint, requestOptions)
    .then((res) => {
      delete abortControllers[endpoint];
      if (res.headers.get('Content-Type') === 'application/json') {
        return res.json();
      } else {
        return res.text();
      }
    })
    .then(
      (result) => {
        return success(result);
      },
      (error) => {
        return failure(error);
      }
    )
    .catch((error) => {
      return failure(error);
    });
}

// Use XMLHttpRequest to make a get call to the API.
// Use ths only if you need to make a call without a timeout.
// For the most part, fetch() will work better, and provide better error handling, among other things.
export const performGetFetchXMLHttp = (endpoint, success, failure, override, printMessage) => {
  if (!endpoint) {
    return;
  }
  endpoint = removeLeadingSlash(endpoint);
  
  // By default we don't want to restart a fetch if one is already running for this endpoint.
  if (!override && abortControllers[endpoint]) {
    if (printMessage) {
      console.warn('Request already in progress: ', endpoint);
    }
    return false;
  }
  
  let fullEndpoint = buildApiUrl(endpoint);
  let xhr = new XMLHttpRequest();

  xhr.onreadystatechange = function () {
    if (this.readyState === 4) {
      if (this.status === 200) {
        success(JSON.parse(this.responseText));
      } else {
        failure(this);
      }
    }
  };

  createAbortController(endpoint, printMessage, xhr)

  xhr.open('GET', fullEndpoint);

  xhr.send();

  return xhr;
}

// Use fetch to make a post call to the API.
export const performPostFetch = (endpoint, requestOptions = {}, success, failure, override,
                                 printMessage, fullEndpoint) => {
  if (!endpoint) {
    return;
  }
  endpoint = removeLeadingSlash(endpoint);
  
  // By default we don't want to restart a fetch if one is already running for this endpoint.
  if (!override && abortControllers[endpoint]) {
    if (printMessage) {
      console.warn('Request already in progress: ', endpoint);
    }
    return false;
  }
  requestOptions.signal = createAbortController(endpoint, printMessage).signal;
  fullEndpoint = fullEndpoint || buildApiUrl(endpoint);
  
  return fetch(fullEndpoint, requestOptions)
    .then((res) => {
      delete abortControllers[endpoint];
      let statusText = res.statusText || '';
      if (!res.ok) {
        return new Error('Network response was not ok: ' + statusText, res);
      }
      if (res.status !== 200) {
        return new Error('Network response status not 200: ' + statusText, res);
      }
      return res.json();
    })
    .then(
      (result) => {
        return success(result);
      },
      (error) => {
        return failure(error);
      }
    )
    .catch((error) => {
      return failure(error);
    });
}

// Use fetch to make a post call to the API.
// This is an alternate version of the fetch post above that inserts the request options into the post body more verbosely.
// Sometimes this one works where the above post function doesn't, and vice versa.
// It is currently unknown why this is, but it would be nice to be able to get rid of one of these functions, likely pending API changes.
export const performPostFetchDeclaredBody = (endpoint, requestOptions = {}, success, failure, override, printMessage) => {
  if (!endpoint) {
    return;
  }
  endpoint = removeLeadingSlash(endpoint);
  
  // By default we don't want to restart a fetch if one is already running for this endpoint.
  if (!override && abortControllers[endpoint]) {
    if (printMessage) {
      console.warn('Request already in progress: ', endpoint);
    }
    return false;
  }

  requestOptions.signal = createAbortController(endpoint, printMessage).signal;
  let fullEndpoint = buildApiUrl(endpoint);
  let contentType = requestOptions instanceof FormData ? 'multipart/form-data' : 'application/json';
  let body = requestOptions instanceof FormData ? requestOptions : JSON.stringify(requestOptions);

  return fetch(fullEndpoint, {
      method: 'POST',
      headers: {'Content-Type': contentType},
      body: body
    })
    .then((res) => {
      delete abortControllers[endpoint];
      let statusText = res.statusText || '';
      if (!res.ok) {
        return new Error('Network response was not ok: ' + statusText, res);
      }
      if (res.status !== 200) {
        return new Error('Network response status not 200: ' + statusText, res);
      }
      return res.json();
    })
    .then(
      (result) => {
        return success(result);
      },
      (error) => {
        return failure(error);
      }
    );
}

export const performFormDataPost = (endpoint, formData, success, failure, progress) => {
  if (!endpoint) {
    return;
  }
  endpoint = removeLeadingSlash(endpoint);
  
  let fullEndpoint = buildApiUrl(endpoint);
  let body = formData;
  let oReq = new XMLHttpRequest();

  oReq.open('POST', fullEndpoint, true);
  oReq.onprogress = function(oEvent){
    if (progress !== undefined) {
      progress(oReq.response);
    }
  }
  oReq.onload = function(oEvent) {
    if (oReq.status === 200) {
      if (success !== undefined) {
        success(JSON.parse(this.responseText));
      }
    } else {
      console.log('Error ' + oReq.status + ' occurred when trying to upload your form.');
      if (failure !== undefined) {
        failure(this);
      }
    }
  };
  oReq.send(body);
  return oReq;
}

export const buildApiUrl = (endpoint) => {
  let baseUrl = window._env_.APP_SECURE_API || window._env_.APP_API || '';
  return baseUrl + endpoint;
}

export const buildSecureApiUrl = (endpoint) => {
  let baseUrl;
  if (window._env_.LOCALHOST) {
    baseUrl = window._env_.APP_API;
  } else {
    baseUrl = window._env_.APP_API.replace('/api', '/secure/api') || '';
  }
  console.warn(baseUrl + endpoint);
  return baseUrl + endpoint;
}

export const buildSecureCohortApiUrl = (endpoint) => {
  let baseUrl = window._env_.CH_API.replace('/clearing-house', '/secure/clearing-house') || '';
  return baseUrl + endpoint;
}

export const showUserGuide = (target) => {
  OpenDocumentsPage(null, 'helpDoc', target)
}

export const createAbortController = (name, printMessage, xhr) => {
  // Clear existing abort controller with this name/endpoint
  abortFetch(name, printMessage);

  if (xhr) {
    abortControllers[name] = xhr;
  } else {
    abortControllers[name] = new AbortController();
  }

  return abortControllers[name];
}

export const getAbortControllers = () => {
  return abortControllers;
}

export const abortFetch = (name, printMessage) => {
  // If the named abort controller already exists, assume its fetch is still ongoing, and abort it
  if (abortControllers[name] && abortControllers[name].abort) {
    abortControllers[name].abort();
    abortControllers[name] = undefined;

    if (printMessage) {
      console.warn('Previous request aborted: ', name);
    }
    
    return true;
  }

  if (printMessage) {
    console.warn('No abortable request by name: ', name);
  }
  return false;
}

export const GotoNewPage = (endpoint) => {
  if (endpoint) {
    window.open(endpoint, '_blank').focus();
  }
}

export function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export async function pause(i) {
    await sleep(i * 1000);
}

export function openWindowWithPost(url, data) {
    console.log('oWwP', url, isChromeBrowser());
    let form = document.createElement('form');
    if (isChromeBrowser()) {
        form.target = '_blank_';
        window.open('', form.target);
    } else {
        form.target = '_blank'
    }
    form.method = 'POST';
    form.action = url;
    form.style.display = 'none';
    form.rel = 'noopener,noreferrer';

    for (let key in data) {
        let input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = data[key];
        form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
}

export const isChromeBrowser = () => {
  let isChromium = window.chrome;
  let winNav = window.navigator;
  let vendorName = winNav.vendor;
  let isOpera = typeof window.opr !== "undefined";
  let isIEedge = winNav.userAgent.indexOf("Edg") > -1;
  let isIOSChrome = winNav.userAgent.match("CriOS");

  if (isIOSChrome) {
     // is Google Chrome on IOS
    return true
  } else if(
    isChromium !== null &&
    typeof isChromium !== "undefined" &&
    vendorName === "Google Inc." &&
    isOpera === false &&
    isIEedge === false
  ) {
     // is Google Chrome
    return true
  } else {
     // not Google Chrome
    return false
  }
}
